// components/Register.tsx - ユーザー登録画面コンポーネント

import React, { useState } from 'react';
import {
Container,
Paper,
Typography,
TextField,
Button,
Box,
Alert,
CircularProgress,
Link
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { normalizeEmail } from '../utils/normalizeEmail';
import PasswordTextField from './PasswordTextField';

const Register: React.FC = () => {
const [username, setUsername] = useState('');
const [hospitalName, setHospitalName] = useState('');
const [email, setEmail] = useState('');
const [password, setPassword] = useState('');
const [confirmPassword, setConfirmPassword] = useState('');
const [error, setError] = useState('');
const [isLoading, setIsLoading] = useState(false);
const [registered, setRegistered] = useState(false);

const { register } = useAuth();
const navigate = useNavigate();

const validateForm = () => {
    if (!username || !email || !password || !confirmPassword) {
        setError('すべての項目を入力してください。');
        return false;
    }

    if (username.length > 15) {
        setError('ユーザー名は15文字以内で入力してください。');
        return false;
    }

    if (hospitalName && hospitalName.length > 20) {
        setError('病院名は20文字以内で入力してください。');
        return false;
    }

    if (password !== confirmPassword) {
        setError('パスワードが一致しません。');
        return false;
    }

    // 簡易的なメールバリデーション
    const normalizedEmail = normalizeEmail(email);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
        setError('有効なメールアドレスを入力してください。');
        return false;
    }

    // パスワードの長さチェック
    if (password.length < 8) {
        setError('パスワードは8文字以上である必要があります。');
        return false;
    }

    // パスワードの複雑さチェック（英数記号から2種類以上）
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSymbol = /[^A-Za-z0-9]/.test(password);

    // 少なくとも2種類の文字タイプが含まれているかをチェック
    const typeCount = [hasLetter, hasNumber, hasSymbol].filter(Boolean).length;
    if (typeCount < 2) {
        setError('パスワードは英字・数字・記号のうち、少なくとも2種類を含む必要があります。');
        return false;
    }

    return true;
};

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) {
      return;
    }
    setIsLoading(true);

    const normalizedEmail = normalizeEmail(email);

    try {
      const response = await register(username, normalizedEmail, password, hospitalName || undefined);
      setRegistered(true);
      // 認証コード入力画面へemailとregistrationフラグを渡して遷移
      navigate('/verify', { state: { email: normalizedEmail, registration: true, mode: 'registration', verificationChallengeId: response.verificationChallengeId } });
    } catch (err: any) {
      if (err.response?.data?.detail === 'registration_failed') {
        setError('登録処理に失敗しました。時間をおいて再度お試しください。');
      } else {
        setError('登録に失敗しました。後でもう一度お試しください。');
      }
    } finally {
      setIsLoading(false);
    }
  };

return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
    <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" align="center" gutterBottom>
        ユーザー登録
        </Typography>

        {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
            {error}
        </Alert>
        )}

        {registered ? (
        <Alert severity="success" sx={{ mb: 3 }}>
            登録が完了しました！認証コード入力画面に移動します...
        </Alert>
        ) : (
        <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="ユーザー名"
            name="username"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isLoading}
            inputProps={{ maxLength: 15 }}
            />
            <TextField
            margin="normal"
            fullWidth
            id="hospitalName"
            label="病院名または代表者様氏名（任意）"
            name="hospitalName"
            value={hospitalName}
            onChange={(e) => setHospitalName(e.target.value)}
            disabled={isLoading}
            inputProps={{ maxLength: 20 }}
            />
            <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="メールアドレス"
            name="email"
            autoComplete="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            />
            <PasswordTextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="パスワード"
            type="password"
            id="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            helperText="8文字以上で、英字・数字・記号のうち2種類以上を含む必要があります"
            />
            <PasswordTextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="パスワード（確認）"
            type="password"
            id="confirmPassword"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={isLoading}
            />

            <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            disabled={isLoading}
            >
            {isLoading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
                '登録'
            )}
            </Button>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
                既にアカウントをお持ちですか？{' '}
                <Link component={RouterLink} to="/login">
                ログイン
                </Link>
            </Typography>
            </Box>
        </Box>
        )}
    </Paper>

    <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
        登録後、サブスクリプションを購入する必要があります。
        </Typography>
    </Box>
    </Container>
);
};

export default Register;
